import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {BoxTraverser} from '@mintdata-wsp/common/controllers/surface/BoxVisitor';
import {SurfaceAreaController as BaseSurfaceAreaController} from '@mintdata-wsp/common/controllers/surface/SurfaceAreaController';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {
    LayoutRecalc,
    LayoutRecalcDimensionsProps,
    LayoutUtils
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LayoutUtils';
import {Overlay} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {Page, PageId, PageName} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RotationOrigin} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationOrigin';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {
    MouseOverRef,
    SelectionInteractionMode,
    SurfaceBounds,
    SurfaceCopyPasteState,
    SurfaceDropContext,
    SurfaceInteractionMode,
    SurfacePoint,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {IBoxMoveState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BoxMainFieldPaths, BoxStateFieldPaths} from '@mintdata/mint-components/components/box/model/BoxBindGroups';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {HorizontalAlignment} from '@mintdata/mint-kit/api/models/HorizontalAlignment';
import {BoxHostId, BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Overflow} from '@mintdata/mint-kit/api/models/Overflow';
import {PageInfo} from '@mintdata/mint-kit/api/models/PageInfo';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {UnitsType} from '@mintdata/mint-kit/api/models/UnitsType';
import {VerticalAlignment} from '@mintdata/mint-kit/api/models/VerticalAlignment';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import bind from 'bind-decorator';
import {action} from 'mobx';

const BOX_COMPONENT_ID = 'mintdata:mint-components:Box';

type SurfaceAreaListener = {
    onMouseMove():void,
    onBoxDragStart():void,
    onBoxDragInProgress():void,
    onBoxDragStop():void,
    onPanPositionChanged():void,
}

export class SurfaceAreaController extends BaseSurfaceAreaController implements HasEvents<SurfaceAreaListener>
{
    private static GRID_SNAP_X_PIXELS:number = 32;
    private static GRID_SNAP_Y_PIXELS:number = 32;

    public getActivePage():Page | null
    {
        const state:SurfaceState = this.getState();

        return state ? state.activePage : null;
    }

    public getCurrentLayerPage():Page | null
    {
        const state:SurfaceState = this.getState();

        return state ? state.currentLayerPage : null;
    }

    // ********  PAGES   ********

    @action.bound
    public addNewPage():void
    {
        const state:SurfaceState = this.getState();

        const {componentDesc} = state.activePage.artboard.viewport;

        const page = Page.create(this._generateNewPageName());
        const viewport:Box = Box.createViewport(page);

        viewport.componentDesc.layoutLeftProp.setInnerValue(componentDesc.layoutLeft);
        viewport.componentDesc.layoutTopProp.setInnerValue(componentDesc.layoutTop);
        viewport.componentDesc.layoutWidthProp.setInnerValue(componentDesc.layoutWidth);
        viewport.componentDesc.layoutHeightProp.setInnerValue(componentDesc.layoutHeight);

        page.artboard.addBox(viewport);
        state.addPage(page);
        controllers.ui.surfaceSelection.addPageToSelectedBoxesIdsByPage(page.id);

        this.setActivePage(page);
    }

    @action.bound
    public renamePage(pageId:PageId, name:string):boolean
    {
        const state:SurfaceState = this.getState();
        let isNameUnique:boolean = this.isPageNameUnique(pageId, name);

        isNameUnique && state.renamePage(pageId, name);

        return isNameUnique;
    }

    @bind
    public isPageNameUnique(pageId:PageId, name:string):boolean
    {
        const state:SurfaceState = this.getState();
        let isNameUnique:boolean = true;

        state.pages.forEach((page:Page) =>
        {
            if( page.id !== pageId && page.name === name )
                isNameUnique = false;
        });

        return isNameUnique;
    }

    public isPageRemoveAvailable():boolean
    {
        const state:SurfaceState = this.getState();
        return state.pages.size > 1;
    }

    @action.bound
    public removeAllPagesFromSection(sectionName:string):void
    {
        const state:SurfaceState = this.getState();
        const pagesToRemove = state.pagesItemsOrdered.filter(page => page.sectionName === sectionName);
        pagesToRemove.forEach(page =>
            this.removePage(page.id)
        );
    }

    @action.bound
    public removePage(pageToRemoveId:PageId):void
    {
        const state:SurfaceState = this.getState();

        if( !pageToRemoveId || !this.isPageRemoveAvailable() )
        {
            console.warn('Cannot remove the last page');
            return;
        }

        const pages:Array<Page> = Array.from(state.pages.values());
        let idToSwitch:PageId;

        for( let i = 0; i < pages.length; i++ )
        {
            const page:Page = pages[i];
            if( page.id !== pageToRemoveId )
            {
                if( (pageToRemoveId === state.activePage.id || pageToRemoveId === state.startPageId) && !idToSwitch )
                    idToSwitch = page.id;

                for( let j:number = page.overlays.length - 1; j > -1; j-- )
                {
                    if( page.overlays[j].page.id === pageToRemoveId )
                        this.removeOverlay(page.overlays[j], page);
                }
            }
        }

        if( pageToRemoveId === state.activePage.id )
        {
            this.setActivePage(state.pages.get(idToSwitch));
        }

        if( pageToRemoveId === state.startPageId )
        {
            this.markPageAsStarted(idToSwitch);
        }

        const page:Page = state.pages.get(pageToRemoveId);
        page.artboard.boxes.forEach((box:Box) => this.removeVisualComponent(box));

        state.removePage(pageToRemoveId);
        controllers.ui.surfaceSelection.removePageFromSelectedBoxesIdsByPage(pageToRemoveId);
    }

    public setActivePage(page:Page, fireEvent:boolean = true):void
    {
        commonControllers.designState.setActivePage(page, false);

        if( fireEvent )
            commonControllers.designState.onActivePageChanged();
    }

    @action.bound
    public async dropComponent(dropContext:SurfaceDropContext,
                               runInActionAfterBoxAdded?:(box:Box) => void):Promise<Box>
    {
        const {surfaceAreaDomElement} = rootState.ui.visualDesigner.surfaceEditor.area;

        if( !surfaceAreaDomElement )
        {
            console.warn(`SurfaceArea has not mounted yet, but SurfaceAreaController.addNewVisualComponent() was already invoked on SurfaceAreaController; taking no action`);
            return null;
        }

        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const {page, parentBox, parentRepeater, parentHostId, point, componentToAdd} = dropContext;

        const positionWithinCanvas = this.computePositionWithinCanvas(point);
        const componentSize = this.componentInitialSize(componentToAdd);

        let parentBoxX:number = 0;
        let parentBoxY:number = 0;

        if( parentBox )
        {
            const hostRef:HTMLElement = parentRepeater && this.getRepeatedBoxHostRef(parentBox, parentRepeater, parentHostId);
            const parentRef:HTMLElement = parentRepeater && this.getRepeatedBoxRef(parentBox, parentRepeater);

            if( hostRef )
            {
                const hostRefPoint:Point = this.getBoxRefAbsolutePoint(hostRef);

                parentBoxX = hostRefPoint.x;
                parentBoxY = hostRefPoint.y;
            }
            else if( parentRef )
            {
                const parentRefPoint:Point = this.getBoxRefAbsolutePoint(parentRef);

                parentBoxX = parentRefPoint.x;
                parentBoxY = parentRefPoint.y;
            }
            else
            {
                parentBoxX = parentBox.absoluteBounds.x;
                parentBoxY = parentBox.absoluteBounds.y;
            }
        }

        return await this.addNewBox(
            page,
            parentBox,
            parentHostId,
            componentToAdd,
            null,
            SurfacePoint.from(
                Math.round(positionWithinCanvas.x / zoom - parentBoxX - componentSize.width / 2),
                Math.round(positionWithinCanvas.y / zoom - parentBoxY - componentSize.height / 2)
            ),
            componentSize,
            (box:Box) =>
            {
                if( runInActionAfterBoxAdded )
                    runInActionAfterBoxAdded(box);

                this.focusSurfaceArea();
            }
        );
    }

    @action
    public focusSurfaceArea():void
    {
        const {surfaceAreaDomElement} = rootState.ui.visualDesigner.surfaceEditor.area;

        if( surfaceAreaDomElement )
            surfaceAreaDomElement.focus();
    }

    @bind
    public markPageAsStarted(pageId:PageId):void
    {
        const state:SurfaceState = this.getState();

        if( state && pageId )
        {
            state.setStartPage(pageId);
        }
    }

    @action.bound
    public duplicatePage(pageId:PageId):void
    {
        const state:SurfaceState = this.getState();
        const pageToCopy:Page = state.pages.get(pageId);
        const newPage:Page = Page.create(this._generateNewPageName(pageToCopy.name));

        state.addPage(newPage);
        controllers.ui.surfaceSelection.addPageToSelectedBoxesIdsByPage(newPage.id);

        pageToCopy.artboard.boxes.forEach((box:Box) =>
        {
            this.cloneBox(pageToCopy, newPage, null, box, this.getBoxName(box));
        });

        this.setActivePage(state.pages.get(newPage.id));
    }

    @action.bound
    public moveAfterPage(selectedPageId:PageId, afterPageId:PageId):void
    {
        const state:SurfaceState = this.getState();
        const afterPageIndex:number = state.pagesItemsOrdered.findIndex(({id}) => id === afterPageId);

        const anchorPage = state.pagesItemsOrdered[afterPageIndex];
        const movedPage = state.pages.get(selectedPageId);
        movedPage.sectionName = anchorPage.sectionName;

        state.movePage(selectedPageId, afterPageIndex + 1);
    }

    @action.bound
    public moveBeforePage(selectedPageId:PageId, beforePageId:PageId):void
    {
        const state:SurfaceState = this.getState();
        const beforePageIndex:number = state.pagesItemsOrdered.findIndex(({id}) => id === beforePageId);

        const anchorPage = state.pagesItemsOrdered[beforePageIndex];
        const movedPage = state.pages.get(selectedPageId);
        movedPage.sectionName = anchorPage.sectionName;

        state.movePage(selectedPageId, beforePageIndex);
    }

    public getPageById(pageId:string):Page
    {
        const state:SurfaceState = this.getState();

        return state ? state.pagesItemsOrdered.find((page:Page) => page.id === pageId) : null;
    }

    // ********  OVERLAYS   ********

    public getIndexInOverlays(overlay:Overlay, overlaysHolder:Page):number
    {
        for( let i = 0; i < overlaysHolder.overlays.length; i++ )
        {
            if( overlaysHolder.overlays[i] === overlay )
                return i;
        }

        return -1;
    }

    public showOverlay(pageId:PageId):void
    {
        const page:Page = this.getState().pages.get(pageId);
        commonControllers.designState.showOverlay(page, false);
    }

    @action.bound
    public setActiveOverlay(overlay:Overlay, pageId:PageId):void
    {
        const page:Page = this.getState().pages.get(pageId);

        page.setActiveOverlay(overlay || null);
    }

    @action.bound
    public removeAllPinnedOverlaysFromPage(pageId:PageId):void
    {
        const state:SurfaceState = this.getState();
        const page:Page = state.pages.get(pageId);

        state.setAllowPagesAnimation(false);
        page.removeOverlaysByPredicate(({isPinned}:Overlay) => isPinned);
    }

    @action.bound
    public removeAllUnpinnedOverlaysFromPage(pageId:PageId):void
    {
        const state:SurfaceState = this.getState();
        const page:Page = state.pages.get(pageId);

        state.setAllowPagesAnimation(false);
        page.removeOverlaysByPredicate(({isPinned}:Overlay) => !isPinned);
    }

    public removeOverlay(overlayToRemove:Overlay, page?:Page):void
    {
        const state:SurfaceState = this.getState();

        if( !page )
            page = this.getActivePage();

        if( overlayToRemove.isPinned )
        {
            state.setAllowPagesAnimation(false);
            page.removeOverlaysByPredicate((overlay:Overlay) => overlayToRemove === overlay);
        }
        else
        {
            const index:number = this.getIndexInOverlays(overlayToRemove, page);
            commonControllers.designState.hideOverlay(index, false, false, false, page);
        }
    }

    @action.bound
    public togglePinOverlay(overlay:Overlay):void
    {
        overlay.setIsPinned(!overlay.isPinned);
    }

    @action.bound
    public makeAllPinnedOverlaysVisible(pageId:PageId):void
    {
        const page:Page = this.getState().pages.get(pageId);

        page.overlays.forEach((overlay:Overlay) => overlay.isPinned && overlay.setIsHidden(false));
    }

    @action.bound
    public makeAllUnpinnedOverlaysVisible(pageId:PageId):void
    {
        const page:Page = this.getState().pages.get(pageId);

        page.overlays.forEach((overlay:Overlay) => !overlay.isPinned && overlay.setIsHidden(false));
    }

    @action.bound
    public makeAllPinnedOverlaysHidden(pageId:PageId):void
    {
        const page:Page = this.getState().pages.get(pageId);

        page.overlays.forEach((overlay:Overlay) => overlay.isPinned && overlay.setIsHidden(true));
    }

    @action.bound
    public makeAllUnpinnedOverlaysHidden(pageId:PageId):void
    {
        const page:Page = this.getState().pages.get(pageId);

        page.overlays.forEach((overlay:Overlay) => !overlay.isPinned && overlay.setIsHidden(true));
    }

    @action.bound
    public makeOverlayVisible(overlay:Overlay):void
    {
        overlay.setIsHidden(false);
    }

    @action.bound
    public makeOverlayHidden(overlay:Overlay):void
    {
        overlay.setIsHidden(true);
    }

    @action.bound
    public moveOverlay(overlay:Overlay, insertIndex:number):void
    {
        const activePage:Page = this.getActivePage();

        activePage.moveOverlay(overlay, insertIndex);
    }

    // ********  SURFACE   ********

    public isInPreviewMode():boolean
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        return area.interactionMode === SurfaceInteractionMode.Preview;
    }

    public isInZenPreviewMode():boolean
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        return area.interactionMode === SurfaceInteractionMode.ZenPreview;
    }

    public isInPreviewOrZenPreviewMode():boolean
    {
        return this.isInPreviewMode() || this.isInZenPreviewMode();
    }

    public isPanEnabled():boolean
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        return area.panModeState.isEnabled;
    }

    public getPanPosition():Point
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        return area.panModeState.position;
    }

    public adjustSurfacePointToPanZoom(point:SurfacePoint):SurfacePoint
    {
        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const panPosition:Point = this.getPanPosition();

        return SurfacePoint.from(
            point.x - panPosition.x * zoom,
            point.y - panPosition.y * zoom
        );
    }

    @action.bound
    public copyBoxesToClipboard():void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;
        const {surfaceSelection} = controllers.ui;
        const currentLayerPage:Page = this.getCurrentLayerPage();

        area.surfaceCopyPasteState = new SurfaceCopyPasteState(
            currentLayerPage.id,
            surfaceSelection.getOrderedSelectedBoxes().reduce((boxes:BoxId[], box:Box) =>
            {
                !box.isViewport && boxes.push(box.id);
                return boxes;
            }, [])
        );
    }

    @action.bound
    public pasteBoxesFromClipboard():void
    {
        const {surfaceCopyPasteState} = rootState.ui.visualDesigner.surfaceEditor.area;
        const {surfaceSelection} = controllers.ui;

        if( surfaceCopyPasteState )
        {
            const state:SurfaceState = this.getState();
            const {pageId: pageFromId} = surfaceCopyPasteState;
            const pageFrom:Page = state && state.pages.get(pageFromId);
            const pageToPaste:Page = this.getCurrentLayerPage();
            let newlyPastedBoxes:Box[] = [];

            pageFrom && surfaceCopyPasteState.boxesIds.forEach((boxId:BoxId) =>
            {
                const box:Box = pageFrom.artboard.allBoxes.get(boxId);

                if( box && box.component.ref.isResolved )
                {
                    let newBounds:SurfaceBounds = null;
                    const newParent:Box = this._isBoxChildOfViewport(box) ? pageToPaste.artboard.viewport : null;

                    if( newParent && !box.parent.isViewport )
                    {
                        const viewportFromBounds:Bounds = pageFrom.artboard.viewport.bounds;
                        const boxAbsoluteBounds:Bounds = box.absoluteBounds;

                        newBounds = SurfaceBounds.from(
                            boxAbsoluteBounds.x - viewportFromBounds.x,
                            boxAbsoluteBounds.y - viewportFromBounds.y,
                            box.componentDesc.layoutWidth.value.value,
                            box.componentDesc.layoutHeight.value.value
                        );
                    }
                    else if( !newParent && box.parent )
                    {
                        const boxAbsoluteBounds:Bounds = box.absoluteBounds;

                        newBounds = SurfaceBounds.from(
                            boxAbsoluteBounds.x,
                            boxAbsoluteBounds.y,
                            box.componentDesc.layoutWidth.value.value,
                            box.componentDesc.layoutHeight.value.value
                        );
                    }

                    newlyPastedBoxes.push(this.cloneBox(pageFrom, pageToPaste, newParent, box, null, true, newBounds));
                }
            });

            surfaceSelection.setMultipleBoxSelection(newlyPastedBoxes);
        }
    }

    public boxHasSelectedParent(box:Box):boolean
    {
        const {surfaceSelection} = controllers.ui;

        let parent = box.parent;

        while( parent )
        {
            if( surfaceSelection.isBoxSelected(parent) )
                return true;

            parent = parent.parent;
        }

        return false;
    }

    public boxHasSelectedChild(box:Box):boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( box.children.length )
        {
            const allChildren = this.flattenBoxesDeep(box.children);

            return !allChildren.every((target:Box) => !surfaceSelection.isBoxSelected(target));
        }

        return false;
    }

    public boxHasChild(box:Box, child:Box):boolean
    {
        if( box.children.length )
        {
            const allChildren = this.flattenBoxesDeep(box.children);

            return allChildren.some((target:Box) => target === child);
        }

        return false;
    }

    public findClosestBoxContainer(box:Box):Box
    {
        let parentBox:Box = box.parent;

        while( parentBox )
        {
            if( this.isBoxContainer(parentBox) )
                return parentBox;

            parentBox = parentBox.parent;
        }

        return null;
    }

    public findClosestBoxByElement(page:Page, currentElement:Element):Box | null
    {
        if( currentElement && currentElement.getAttribute )
        {
            const boxId = currentElement.getAttribute('data-box-id');

            if( boxId != null )
                return this.findBoxById(page.id, boxId);
        }

        if( currentElement )
            return this.findClosestBoxByElement(page, currentElement.parentElement);

        return null;
    }

    /**
     * Returns true if the boxes at least partially overlap the query box.
     */
    public areBoxesOverlapped(boxes:Box[], query:Box):boolean
    {
        if( !query )
            return true;

        return boxes.every((box:Box) =>
        {
            const intersection:Bounds = Bounds.intersect(box.absoluteBounds, query.absoluteBounds);

            return !intersection.isEqualTo(Bounds.newZero());
        });
    }

    public findBoxesInBounds(bounds:Bounds):Box[]
    {
        let boxesInBounds:Box[] = [];
        const currentLayerPage:Page = this.getCurrentLayerPage();

        if( !currentLayerPage )
            return boxesInBounds;

        const {boxes} = currentLayerPage.artboard;
        const {children: viewportChildren} = currentLayerPage.artboard.viewport;

        boxes.forEach((box:Box):void =>
        {
            if( !box.isViewport )
            {
                const boxBounds = box.absoluteBounds;
                const intersection:Bounds = Bounds.intersect(boxBounds, bounds);

                !intersection.isEqualTo(Bounds.newZero()) && boxesInBounds.push(box);
            }
        });

        viewportChildren.forEach((box:Box):void =>
        {
            const boxBounds = box.absoluteBounds;
            const intersection:Bounds = Bounds.intersect(boxBounds, bounds);

            !intersection.isEqualTo(Bounds.newZero()) && boxesInBounds.push(box);
        });

        return boxesInBounds;
    }

    public getBoxIndex(box:Box, absolute:boolean = false):number
    {
        let boxes = this.getBoxParentChildren(box);

        if( box.hostId )
            boxes = boxes.filter((childBox:Box) => childBox.hostId === box.hostId);

        const index = boxes.findIndex(({id}) => id === box.id);

        if( absolute && box.parent )
            return this.getBoxParentChildren(box.parent).length + index;

        return index;
    }

    public getBoxLevel(box:Box):number
    {
        let level = 1;

        BoxTraverser.visit(
            box,
            {onBox: (parent:Box) => level += parent.componentDesc.isMultiHostContainer ? 2 : 1},
            true
        );

        return level;
    }

    public getBoxParentChildren(box:Box):Box[]
    {
        return box.parent ? box.parent.children : box.page.artboard.boxes;
    }

    public isBoxRoot(box:Box):boolean
    {
        return this.getBoxLevel(box) === 1;
    }

    public isBoxFirst(box:Box):boolean
    {
        return this.getBoxIndex(box) === 0;
    }

    public isBoxFirst1(box:Box):boolean
    {
        return this.getBoxIndex(box) === 0
            && this.isBoxInFirstHost(box)
            && (!box.hostId
                || (box.parent && this.isFirstHost(box.parent, box.hostId)));
    }

    public isBoxInFirstHost(box:Box):boolean
    {
        let isBoxInFirstHost:boolean = true;
        let prevHostId:BoxHostId = box.hostId;

        BoxTraverser.visit(
            box,
            {
                onBox: (parent:Box) =>
                {
                    const {componentDesc: {containerHostIds}} = parent;

                    if( prevHostId && containerHostIds.indexOf(prevHostId) !== containerHostIds.length - 1 )
                        isBoxInFirstHost = false;

                    prevHostId = parent.hostId;
                }
            },
            true
        );

        return isBoxInFirstHost;
    }

    public isFirstHost(box:Box, hostId:BoxHostId):boolean
    {
        const {componentDesc: {containerHostIds}} = box;

        if( hostId && containerHostIds.indexOf(hostId) === containerHostIds.length - 1 )
            return true;

        return false;
    }

    public isBoxLast(box:Box):boolean
    {
        let boxes = this.getBoxParentChildren(box);

        if( box.hostId )
            boxes = boxes.filter((childBox:Box) => childBox.hostId === box.hostId);

        return this.getBoxIndex(box) === boxes.length - 1;
    }

    public areBoxesSiblings(boxes:Box[]):boolean
    {
        const parent:Box = boxes[0].parent;
        const hostId:BoxHostId = boxes[0].hostId;

        return boxes.every(box => box.parent === parent && box.hostId === hostId);
    }

    public areBoxesSequential(boxes:Box[]):boolean
    {
        if( !this.areBoxesSiblings(boxes) )
            return false;

        return boxes.every((box, index, collection) =>
        {
            return !index || (this.getBoxIndex(box) - this.getBoxIndex(collection[index - 1]) === 1);
        });
    }

    public isBoxComponent(box:Box):boolean
    {
        return box.component.ref.componentId === BOX_COMPONENT_ID;
    }

    public isBoxContainer(box:Box):boolean
    {
        return !!box.componentDesc.containerHostIds;
    }

    public isBoxOnCurrentLayerPage(box:Box):boolean
    {
        return box.page === this.getCurrentLayerPage();
    }

    public isCutActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.getSelectedBoxes().length > 0;
    }

    public isCopyActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.getSelectedBoxes().length > 0;
    }

    public isPasteActionAvailable():boolean
    {
        const {surfaceCopyPasteState} = rootState.ui.visualDesigner.surfaceEditor.area;

        return !!surfaceCopyPasteState;
    }

    public isDeleteActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.getSelectedBoxes().length > 0 && !surfaceSelection.hasViewportInSelected();
    }

    public isCloneActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.getSelectedBoxes().length > 0 && !surfaceSelection.isOnlyViewportSelected();
    }

    public isBringToFrontActionAvailable():boolean
    {
        return this.isBringForwardActionAvailable();
    }

    public isBringForwardActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

        if( !this.areBoxesSiblings(selectedBoxes) )
            return false;

        if( this.areBoxesSequential(selectedBoxes) && this.isBoxLast(selectedBoxes[selectedBoxes.length - 1]) )
            return false;

        return true;
    }

    public isSendToBackActionAvailable():boolean
    {
        return this.isSendBackwardActionAvailable();
    }

    public isSendBackwardActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

        if( !this.areBoxesSiblings(selectedBoxes) )
            return false;

        if( this.areBoxesSequential(selectedBoxes) && this.isBoxFirst(selectedBoxes[0]) )
            return false;

        return true;
    }

    public isAlignmentActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        return this.areBoxesSiblings(selectedBoxes);
    }

    public isDistributionActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        return this.areBoxesSiblings(selectedBoxes) && selectedBoxes.length > 2;
    }

    public isGroupActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length < 1 || !this.areBoxesSiblings(selectedBoxes) || surfaceSelection.hasViewportInSelected() )
            return false;

        const boxComponent = this.findBoxVisualComponent();

        return !!boxComponent;
    }

    public isUngroupActionAvailable()
    {
        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length !== 1 || surfaceSelection.hasViewportInSelected() )
            return false;

        const boxComponent = this.findBoxVisualComponent();

        if( !boxComponent )
            return false;

        const groupBox = selectedBoxes[0];

        if( groupBox.component.ref.componentId !== boxComponent.ref.componentId
            || groupBox.children.length === 0 )
            return false;

        return true;
    }

    public isLockActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length < 1 || surfaceSelection.hasViewportInSelected() ) return false;

        return selectedBoxes.some((box:Box) => !box.componentDesc.lockProp.value.value);
    }

    public isUnlockActionAvailable()
    {
        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length < 1 || surfaceSelection.hasViewportInSelected() ) return false;

        return selectedBoxes.some((box:Box) => box.componentDesc.lockProp.value.value);
    }

    public isLockNestedActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length < 1 )
            return false;

        return selectedBoxes.some((box:Box) =>
        {
            return box.children.some((child:Box) => !child.componentDesc.lockProp.value.value);
        });
    }

    public isUnlockNestedActionAvailable():boolean
    {
        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length < 1 )
            return false;

        return selectedBoxes.some((box:Box) =>
        {
            return box.children.some((child:Box) => child.componentDesc.lockProp.value.value);
        });
    }

    @action.bound
    public lockSelectedBoxes():boolean
    {
        if( !this.isLockActionAvailable() )
            return false;

        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        selectedBoxes.forEach((box:Box) =>
        {
            box.componentDesc.lockProp.setInnerValue(ABoolean.true());
        });
    }

    @action.bound
    public unlockSelectedBoxes():boolean
    {
        if( !this.isUnlockActionAvailable() )
            return false;

        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        selectedBoxes.forEach((box:Box) =>
        {
            box.componentDesc.lockProp.setInnerValue(ABoolean.false());
        });
    }

    @action.bound
    public lockNestedInSelectedBoxes():boolean
    {
        if( !this.isLockNestedActionAvailable() )
            return false;

        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        selectedBoxes.forEach((box:Box) =>
        {
            box.children.forEach((child:Box) =>
            {
                child.componentDesc.lockProp.setInnerValue(ABoolean.true());
            });
        });
    }

    @action.bound
    public unlockNestedInSelectedBoxes():boolean
    {
        if( !this.isUnlockNestedActionAvailable() )
            return false;

        const {surfaceSelection} = controllers.ui;
        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        selectedBoxes.forEach((box:Box) =>
        {
            box.children.forEach((child:Box) =>
            {
                child.componentDesc.lockProp.setInnerValue(ABoolean.false());
            });
        });
    }

    public getBoxesBounds(boxes:Box[], absolute:boolean = false):Bounds
    {
        boxes = boxes.filter((box:Box) => box.componentDesc.isSelfDirected);

        if( !boxes.length )
            return Bounds.newZero();

        let minLeft = Number.MAX_VALUE;
        let minTop = Number.MAX_VALUE;
        let maxRight = -Number.MAX_VALUE;
        let maxBottom = -Number.MAX_VALUE;

        let left:number;
        let top:number;
        let width:number;
        let height:number;

        for( const box of boxes )
        {
            const {layoutLeft, layoutTop, layoutWidth, layoutHeight, rotationProp} = box.componentDesc;

            const {value: {isEnabled: isRotationEnabled, angle, origin}} = rotationProp;

            if( absolute )
            {
                const bounds = box.absoluteBounds;

                top = bounds.y;
                left = bounds.x;
                width = bounds.width;
                height = bounds.height;
            }
            else
            {
                if( LayoutUtils.areLayoutPropsHasPercentages(box) )
                {
                    const parentBounds = LayoutUtils.getParentHostClientRect(box);
                    const boxBounds = LayoutUtils.getBoxClientRect(box);

                    if( parentBounds && boxBounds )
                    {
                        top = boxBounds.top - parentBounds.top;
                        left = boxBounds.left - parentBounds.left;
                        width = boxBounds.width;
                        height = boxBounds.height;
                    }
                    else
                    {
                        top = 0;
                        left = 0;
                        width = 0;
                        height = 0;
                    }
                }
                else
                {
                    top = layoutTop.value.value;
                    left = layoutLeft.value.value;
                    width = layoutWidth.value.value;
                    height = layoutHeight.value.value;
                }
            }

            if( isRotationEnabled.value && angle.value % 360 !== 0 )
            {
                const rotatedBounds = this._rotate(top, left, width, height, angle.value, origin);

                top = rotatedBounds.top;
                left = rotatedBounds.left;
                width = rotatedBounds.width;
                height = rotatedBounds.height;
            }

            const bottom = top + height;
            const right = left + width;

            if( left < minLeft ) minLeft = left;
            if( top < minTop ) minTop = top;
            if( bottom > maxBottom ) maxBottom = bottom;
            if( right > maxRight ) maxRight = right;
        }

        minTop = Math.floor(minTop);
        minLeft = Math.floor(minLeft);
        maxRight = Math.ceil(maxRight);
        maxBottom = Math.ceil(maxBottom);

        return Bounds.from(minLeft, minTop, maxRight - minLeft, maxBottom - minTop);
    }

    public getSnappedPoint(rawX:number, rawY:number):SurfacePoint
    {
        const snapX = SurfaceAreaController.GRID_SNAP_X_PIXELS;
        const snapY = SurfaceAreaController.GRID_SNAP_Y_PIXELS;

        const x = Math.round(rawX / snapX) * snapX;
        const y = Math.round(rawY / snapY) * snapY;

        return new SurfacePoint(x, y);
    }

    @action.bound
    public changeSelectedBoxesOnBoxClick(toggleSelection:boolean, selectSubtree:boolean, box:Box, page?:Page):void
    {
        const {surfaceSelection} = controllers.ui;
        const currentLayerPage:Page = this.getCurrentLayerPage();

        if( !this.areBoxesResolved([box]) || !currentLayerPage )
            return;

        const isOnCurrentLayer:boolean = currentLayerPage.id === box.page.id;

        if( isOnCurrentLayer )
        {
            const isBoxSelected:boolean = surfaceSelection.isBoxSelected(box);

            if( toggleSelection )
            {
                if( isBoxSelected )
                    return surfaceSelection.removeBoxFromSelection(box);

                if( this.boxHasSelectedParent(box) || this.boxHasSelectedChild(box) )
                    return surfaceSelection.setSingleBoxSelection(box);

                surfaceSelection.addBoxToSelection(box);
            }
            else if( selectSubtree )
            {
                if( this.boxHasSelectedParent(box) || this.boxHasSelectedChild(box) )
                    return surfaceSelection.setSingleBoxSelection(box);

                const selectedBoxes:Box[] = surfaceSelection.getSelectedBoxes();
                const lastSelectedBox:Box = selectedBoxes.length ? selectedBoxes[selectedBoxes.length - 1] : null;

                if( !lastSelectedBox || box === lastSelectedBox )
                    return;

                surfaceSelection.addBoxToSelection(box);

                const allBoxes:Box[] = this.getAllBoxes(currentLayerPage, true);

                const boxIndex:number = allBoxes.findIndex((target:Box) => target === box);
                const lastSelectedBoxIndex:number = allBoxes.findIndex((target:Box) => target === lastSelectedBox);

                const startIndex:number = Math.min(boxIndex, lastSelectedBoxIndex);
                const endIndex:number = Math.max(boxIndex, lastSelectedBoxIndex);

                for( let i = startIndex; i <= endIndex; i++ )
                {
                    const target = allBoxes[i];

                    if(
                        this.boxHasSelectedParent(target) ||
                        this.boxHasChild(target, lastSelectedBox) ||
                        this.boxHasChild(target, box)
                    )
                        surfaceSelection.removeBoxFromSelection(target);
                    else
                        surfaceSelection.addBoxToSelection(target);
                }
            }
            else if( !isBoxSelected )
                surfaceSelection.setSingleBoxSelection(box);
        }
        else if( !box.isViewport )
        {
            const activePage:Page = this.getActivePage();
            let activeOverlay:Overlay = null;

            if( activePage.id !== box.page.id )
            {
                activePage.overlays.forEach((overlay:Overlay) =>
                {
                    if( overlay.page.id === box.page.id )
                        activeOverlay = overlay;
                });
            }

            this.setActiveOverlay(activeOverlay, activePage.id);

            surfaceSelection.setSingleBoxSelection(box);
        }
    }

    @action.bound
    public setInteractionMode(interactionMode):void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        this.disablePan();

        area.interactionMode = interactionMode;
    }

    @action.bound
    public togglePreviewMode():void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        this.disablePan();

        area.interactionMode = area.interactionMode === SurfaceInteractionMode.Preview
            ? SurfaceInteractionMode.Edit
            : SurfaceInteractionMode.Preview;
    }

    @action.bound
    public toggleZenPreviewMode():void
    {
        const {ui: {visualDesigner: {surfaceEditor: {area}, lx: {isLxDevMode}}}} = rootState;

        this.disablePan();

        if( area.interactionMode !== SurfaceInteractionMode.ZenPreview )
        {
            this._areaInteractionModeBeforeZenPreview = area.interactionMode;
            area.interactionMode = SurfaceInteractionMode.ZenPreview;

            document.body.className = 'md-body no-back';
        }
        else
        {
            area.interactionMode = this._areaInteractionModeBeforeZenPreview;

            document.body.className = 'md-body';
        }
    }

    private _areaInteractionModeBeforeZenPreview:SurfaceInteractionMode = SurfaceInteractionMode.Edit;

    @action.bound
    public setPanPosition(position:Point, fireEvent:boolean = true):void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        area.panModeState.position = position;

        if( fireEvent )
            EventBus.fire(this, 'panPositionChanged');
    }

    @action.bound
    public enablePan():void
    {
        const {surfaceSelection} = controllers.ui;
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        if( !this.isInPreviewOrZenPreviewMode() &&
            !surfaceSelection.isCursorSelectionEnabled() &&
            !area.panModeState.isEnabled )
            area.panModeState.isEnabled = true;
    }

    @action.bound
    public disablePan():void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        if( area.panModeState.isEnabled )
            area.panModeState.isEnabled = false;
    }

    @action.bound
    public fitPage():void
    {
        // const panTop =
        // SurfaceBody width
        // - VisualDesignerBody__gridRightColumn
        // - VisualDesignerBody__gridLeftPanels
        // - VisualDesignerBody__gridRightPanels
        // - page width
        // - page left
        // / 2

        // const panLeft =
        // SurfaceBody height
        // - page height
        // - page top
        // / 2

        // TODO: set page position to center
        this.setPanPosition(Point.zero());

        // TODO: set zoom to fit page
        this.setZoom(1);
    }

    @action.bound
    public setZoom(zoom:number):void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        area.zoom = zoom;
    }

    @action.bound
    public resetView():void
    {
        const {workbenchVisibility} = controllers.ui;

        const {sampleDesign} = rootState.ui.visualDesigner;
        const isShowOnlyTable:boolean | null = sampleDesign && !sampleDesign.showSurface;

        if( (typeof isShowOnlyTable !== 'boolean' ? false : !isShowOnlyTable)
            || !workbenchVisibility.isSpreadsheetFullScreen()
        )
        {
            const leftPanel = controllers.ui.workbenchVisibility.getGridLeftPanelsDomRef();
            const leftPanelWidth:number = leftPanel ? leftPanel.getBoundingClientRect().width : 0;

            const VIEWPORT_TOP_LEFT_OFFSET = 30;

            const viewport:Box = this.getActivePage().artboard.viewport;
            const viewportBounds:Bounds = viewport.bounds;

            this.setPanPosition(
                Point.from(
                    leftPanelWidth + VIEWPORT_TOP_LEFT_OFFSET - viewportBounds.x,
                    VIEWPORT_TOP_LEFT_OFFSET - viewportBounds.y
                )
            );
            this.setZoom(1);
        }
    }

    @action.bound
    public zoomIn(pivot:Point, zoomPercent?:number):void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;
        const {surfaceAreaDomElement} = area;

        if( !surfaceAreaDomElement )
            return;

        const oldZoom:number = area.zoom;

        // if( area.zoom >= 200 ) area.zoom += 50;
        // else if( area.zoom >= 100 ) area.zoom += 10;
        // else if( area.zoom >= 10 ) area.zoom += 5;
        // else if( area.zoom >= 1 ) area.zoom += 1;
        // else if( area.zoom < 1 ) area.zoom += 0.1;

        if( zoomPercent )
        {
            const zoomFactor = zoomPercent / 100;

            if( area.zoom + zoomFactor <= 10 )
                if( area.zoom < 1 )
                    area.zoom += zoomFactor / 2;
                else
                    area.zoom *= (zoomFactor * 1.5 + 1);
            else
                area.zoom = 10;
        }
        else
        {
            if( oldZoom < 1 )
                area.zoom += 0.1;
            else if( oldZoom >= 1 && oldZoom <= 10 )
                area.zoom *= 1.07;
            else
                return;
        }

        area.zoom = Math.min(10, area.zoom);

        const newZoom:number = area.zoom;

        this._adjustPanPositionAfterZoom(oldZoom, newZoom, pivot);
    }

    @action.bound
    public zoomOut(pivot:Point, zoomPercent?:number):void
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;
        const {surfaceAreaDomElement} = area;

        if( !surfaceAreaDomElement )
            return;

        const oldZoom:number = area.zoom;

        // if( area.zoom >= 250 ) area.zoom -= 50;
        // else if( area.zoom >= 110 ) area.zoom -= 10;
        // else if( area.zoom >= 15 ) area.zoom -= 5;
        // else if( area.zoom > 1 ) area.zoom -= 1;
        // else if( area.zoom > 0.1 ) area.zoom -= 0.1;

        if( zoomPercent )
        {
            const zoomFactor = zoomPercent / 100;

            if( area.zoom - zoomFactor >= 0.1 )
                if( area.zoom < 1 )
                    area.zoom -= zoomFactor / 2;
                else
                    area.zoom /= (zoomFactor * 1.5 + 1);
            else
                area.zoom = 0.1;
        }
        else
        {
            if( oldZoom >= 0.1 && oldZoom <= 1 )
                area.zoom -= 0.1;
            else if( oldZoom > 1 )
                area.zoom /= 1.07;
            else
                return;
        }

        area.zoom = Math.max(0.1, area.zoom);

        const newZoom:number = area.zoom;

        this._adjustPanPositionAfterZoom(oldZoom, newZoom, pivot);
    }

    @action.bound
    public zoomTo(percent:number):void
    {
        const {workbenchVisibility} = controllers.ui;

        const {area} = rootState.ui.visualDesigner.surfaceEditor;
        const {surfaceAreaDomElement} = area;

        if( !surfaceAreaDomElement )
            return;

        const gridGlassDomRef:HTMLElement | null = workbenchVisibility.getGridGlassDomRef();

        if( !gridGlassDomRef )
            return;

        const clientRect:ClientRect = gridGlassDomRef.getBoundingClientRect();

        const pivot = Point.from(
            clientRect.left + (clientRect.width / 2),
            clientRect.top + (clientRect.height / 2)
        );

        const oldZoom:number = area.zoom;

        this.setZoom(percent / 100);

        this._adjustPanPositionAfterZoom(oldZoom, area.zoom, pivot);
    }

    private _adjustPanPositionAfterZoom(oldZoom:number, newZoom:number, pivot:Point):void
    {
        const {surfaceAreaDomElement} = rootState.ui.visualDesigner.surfaceEditor.area;

        if( !surfaceAreaDomElement )
            return;

        const panPosition = this.getPanPosition();

        const surfaceAreaBounds = surfaceAreaDomElement.getBoundingClientRect();

        const pageX = pivot.x;
        const pageY = pivot.y - surfaceAreaBounds.top;

        const oldUnzoomedPageX = pageX / oldZoom;
        const oldUnzoomedPageY = pageY / oldZoom;

        const newUnzoomedPageX = pageX / newZoom;
        const newUnzoomedPageY = pageY / newZoom;

        const realPageX = oldUnzoomedPageX - panPosition.x;
        const realPageY = oldUnzoomedPageY - panPosition.y;

        const newPanX = -(realPageX - newUnzoomedPageX);
        const newPanY = -(realPageY - newUnzoomedPageY);

        const ZOOM_ROUND_THRESHOLD = 0;
        const isFloatXY = newZoom > ZOOM_ROUND_THRESHOLD;

        this.setPanPosition(Point.from(
            isFloatXY ? newPanX : parseFloat(newPanX.toFixed(0)),
            isFloatXY ? newPanY : parseFloat(newPanY.toFixed(0))
        ));
    }

    @action.bound
    public moveIntoBox(movingBox:Box, intoBox:Box | null, hostId:BoxHostId):void
    {
        if( movingBox === intoBox )
            return;

        this._removeMovingBox(movingBox);

        if( intoBox )
            intoBox.addChild(movingBox);
        else
            movingBox.page.artboard.addRootBox(movingBox);

        movingBox.setHostId(hostId);
    }

    @action.bound
    public moveBeforeBox(movingBox:Box, beforeBox:Box):void
    {
        if( movingBox === beforeBox )
            return;

        this._removeMovingBox(movingBox);

        const beforeBoxIndex:number = this.getBoxIndex(beforeBox);

        if( beforeBox.parent )
            beforeBox.parent.addChild(movingBox, beforeBoxIndex);
        else
            movingBox.page.artboard.addRootBox(movingBox, beforeBoxIndex);

        movingBox.setHostId(beforeBox.hostId);
    }

    @action.bound
    public moveAfterBox(movingBox:Box, afterBox:Box):void
    {
        if( movingBox === afterBox )
            return;

        this._removeMovingBox(movingBox);

        const afterBoxIndex:number = this.getBoxIndex(afterBox);

        if( afterBox.parent )
            afterBox.parent.addChild(movingBox, afterBoxIndex + 1);
        else
            movingBox.page.artboard.addRootBox(movingBox, afterBoxIndex + 1);

        movingBox.setHostId(afterBox.hostId);
    }

    @action
    public dragSelectedBoxesBy(dataDeltaX:number, dataDeltaY:number):void
    {
        const {surfaceMoving, surfaceBoxReparenting, surfaceSelection, surfaceArea, surfaceGuidelines} = controllers.ui;

        let {boxDragStartX, boxDragStartY, boxDragDeltaX, boxDragDeltaY} = surfaceMoving.getBoxDrag();

        const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        boxDragDeltaX += dataDeltaX;
        boxDragDeltaY += dataDeltaY;

        const selectionBounds = surfaceSelection.getSelectionBounds();

        if( !selectionBounds )
            return;

        const newSelectionBounds = Bounds.from(
            boxDragStartX + Math.round(boxDragDeltaX / zoom),
            boxDragStartY + Math.round(boxDragDeltaY / zoom),
            selectionBounds.width,
            selectionBounds.height
        );

        surfaceSelection.setSelectionBounds(newSelectionBounds);

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( !selectedBoxes.length || !surfaceArea.areBoxesResolved(selectedBoxes) )
            return;

        const rect = surfaceArea.getBoxesBounds(selectedBoxes).toClientRect();

        const snapBounds = surfaceBoxReparenting.isBoxReparentingEnabled()
            ? surfaceSelection.getSelectionBounds()
            : surfaceGuidelines.getSnapBounds(SelectionInteractionMode.Moving, null);

        if( !snapBounds )
            return;

        const snapRect = snapBounds.toClientRect();

        const deltaX = snapRect.left - rect.left;
        const deltaY = snapRect.top - rect.top;

        const moveBoundsDelta = SurfaceBounds.from(deltaX, deltaY, 0, 0);
        surfaceArea.moveSelectedBoxesBy(moveBoundsDelta);

        surfaceMoving.setBoxDrag(null, null, boxDragDeltaX, boxDragDeltaY);

        surfaceArea.onBoxDragInProgressEvent(selectedBoxes);
    }

    @action.bound
    public moveSelectedBoxesBy(moveBoundsDelta:SurfaceBounds):void
    {
        const {surfaceSelection} = controllers.ui;

        surfaceSelection.getSelectedBoxes().forEach((box:Box) =>
        {
            if( !box.component.ref.resolvedComponent || !box.componentDesc.isSelfDirected )
                return;

            const {layoutLeft, layoutTop} = box.componentDesc;
            const isRecalcX = moveBoundsDelta.position.x !== 0;
            const isRecalcY = moveBoundsDelta.position.y !== 0;
            let parentBounds:ClientRect;

            if( isRecalcX )
            {
                let deltaLeft;
                let left:Units;

                if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
                {
                    if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                    if( !parentBounds ) return;

                    deltaLeft = (moveBoundsDelta.position.x / parentBounds.width) * 100;
                }
                else
                    deltaLeft = moveBoundsDelta.position.x;

                left = new Units(
                    ANumber.fromNumber(layoutLeft.value.value + deltaLeft),
                    layoutLeft.unitsType
                );

                box.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, left);
            }

            if( isRecalcY )
            {
                let deltaTop;
                let top:Units;

                if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
                {
                    if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                    if( !parentBounds ) return;

                    deltaTop = (moveBoundsDelta.position.y / parentBounds.height) * 100;
                }
                else
                    deltaTop = moveBoundsDelta.position.y;

                top = new Units(
                    ANumber.fromNumber(layoutTop.value.value + deltaTop),
                    layoutTop.unitsType
                );

                box.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, top);
            }

            if( isRecalcX )
                LayoutUtils.recalcDimensions(
                    {
                        componentDesc: box.componentDesc,
                        layoutRecalc: LayoutRecalc.MOVED_HORIZONTAL,
                        hostClientRect: parentBounds
                    }
                );

            if( isRecalcY )
                LayoutUtils.recalcDimensions(
                    {
                        componentDesc: box.componentDesc,
                        layoutRecalc: LayoutRecalc.MOVED_VERTICAL,
                        hostClientRect: parentBounds
                    }
                );
        });
    }

    @action.bound
    public resizeBoxesBy(boxes:Box[], sizeDelta:SurfaceSize):void
    {
        boxes.forEach((box:Box) =>
        {
            if( !box.component.ref.resolvedComponent || !box.componentDesc.isSelfDirected )
                return;

            if( sizeDelta.width === 0 && sizeDelta.height === 0 )
                return;

            const parentHostClientRect:ClientRect = LayoutUtils.getParentHostClientRect(box);

            const props:LayoutRecalcDimensionsProps = {
                componentDesc: box.componentDesc,
                hostClientRect: parentHostClientRect,
                layoutRecalc: LayoutRecalc.RESIZE
            };

            if( sizeDelta.width !== 0 )
            {
                const {layoutWidthProp, layoutWidth} = box.componentDesc;

                let deltaWidth:number = sizeDelta.width;

                if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
                {
                    if( !parentHostClientRect )
                        return;

                    deltaWidth = (sizeDelta.width / parentHostClientRect.width) * 100;
                }

                const newWidth = new Units(
                    ANumber.fromNumber(layoutWidth.value.value + deltaWidth),
                    layoutWidth.unitsType
                );

                if( newWidth.value.value > 0 )
                {
                    layoutWidthProp.setPropValue(PropPath.ROOT, newWidth);

                    if( !box.isViewport && parentHostClientRect )
                        LayoutUtils.recalcRight(props);
                }
            }

            if( sizeDelta.height !== 0 )
            {
                const {layoutHeightProp, layoutHeight} = box.componentDesc;

                let deltaHeight:number = sizeDelta.height;

                if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
                {
                    if( !parentHostClientRect )
                        return;

                    deltaHeight = (sizeDelta.height / parentHostClientRect.height) * 100;
                }

                const newHeight = new Units(
                    ANumber.fromNumber(layoutHeight.value.value + deltaHeight),
                    layoutHeight.unitsType
                );

                if( newHeight.value.value > 0 )
                {
                    layoutHeightProp.setPropValue(PropPath.ROOT, newHeight);

                    if( !box.isViewport && parentHostClientRect )
                        LayoutUtils.recalcBottom(props);
                }
            }
        });
    }

    @action.bound
    public cloneSelectedBoxes():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isCloneActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getSelectedBoxes();
        const newlyClonedBoxes:Array<Box> = selectedBoxes.reduce((newBoxes:Box[], box:Box) =>
        {
            !box.isViewport && newBoxes.push(this.cloneBox(box.page, box.page, box.parent, box));
            return newBoxes;
        }, []);

        surfaceSelection.setMultipleBoxSelection(newlyClonedBoxes);

        return true;
    }

    @action.bound
    public groupSelectedBoxesIntoBox():boolean
    {
        const {surfaceSelection, openDesign} = controllers.ui;

        if( !this.isGroupActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getSelectedBoxes();
        const page:Page = selectedBoxes[0].page;
        const parent:Box = selectedBoxes[0].parent;
        const hostId:BoxHostId = selectedBoxes[0].hostId;
        const rootBoxes:Array<Box> = page.artboard.boxes;
        const boxComponent:VisualComponent = this.findBoxVisualComponent();
        const bounds:Bounds = this.getBoxesBounds(selectedBoxes);
        const boxesData:{
            [boxId:string]:{
                left:Units,
                right:Units,
                top:Units,
                bottom:Units,
                width:Units,
                height:Units
            }
        } = {};

        selectedBoxes.forEach((box:Box) =>
        {
            const boxBounds:Bounds = this.getBoxesBounds([box]);
            const parentBounds = LayoutUtils.getParentHostClientRect(box);

            const {
                layoutLeft,
                layoutTop,
                layoutRight,
                layoutBottom,
                layoutWidth,
                layoutHeight
            } = box.componentDesc;
            let leftValue:number;
            let topValue:number;
            let rightValue:number;
            let bottomValue:number;
            let widthValue:number;
            let heightValue:number;

            if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
                leftValue = (boxBounds.x / parentBounds.width - bounds.x / parentBounds.width) * 100;
            else
                leftValue = boxBounds.x - bounds.x;

            if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
                topValue = (boxBounds.y / parentBounds.height - bounds.y / parentBounds.height) * 100;
            else
                topValue = boxBounds.y - bounds.y;

            if( UnitsType.percentages().isEqualTo(layoutRight.unitsType) )
                rightValue = (
                    layoutRight.value.value - (
                        parentBounds.width - bounds.x - bounds.width
                    ) * 100 / parentBounds.width
                ) * parentBounds.width / bounds.width;
            else
                rightValue = bounds.width + bounds.x - boxBounds.width - boxBounds.x;

            if( UnitsType.percentages().isEqualTo(layoutBottom.unitsType) )
                bottomValue = (
                    layoutBottom.value.value - (
                        parentBounds.height - bounds.y - bounds.height
                    ) * 100 / parentBounds.height
                ) * parentBounds.height / bounds.height;
            else
                bottomValue = bounds.height + bounds.y - boxBounds.height - boxBounds.y;

            if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
                widthValue = boxBounds.width * 100 / bounds.width;
            else
                widthValue = boxBounds.width;

            if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
                heightValue = boxBounds.height * 100 / bounds.height;
            else
                heightValue = boxBounds.height;

            const left:Units = new Units(ANumber.fromNumber(leftValue), layoutLeft.unitsType);
            const top:Units = new Units(ANumber.fromNumber(topValue), layoutTop.unitsType);
            const right:Units = new Units(ANumber.fromNumber(rightValue), layoutRight.unitsType);
            const bottom:Units = new Units(ANumber.fromNumber(bottomValue), layoutBottom.unitsType);
            const width:Units = new Units(ANumber.fromNumber(widthValue), layoutWidth.unitsType);
            const height:Units = new Units(ANumber.fromNumber(heightValue), layoutHeight.unitsType);

            boxesData[box.id] = {
                left,
                right,
                top,
                bottom,
                width,
                height
            };
        });

        const box:Box = Box.createVisualBox(
            boxComponent,
            page,
            parent,
            [],
            hostId
        );

        const name:string = this._generateNewBoxName(page, box);

        box.componentDesc.layoutLeftProp.setInnerValue(Units.fromPixels(bounds.x));
        box.componentDesc.layoutTopProp.setInnerValue(Units.fromPixels(bounds.y));
        box.componentDesc.layoutWidthProp.setInnerValue(Units.fromPixels(bounds.width));
        box.componentDesc.layoutHeightProp.setInnerValue(Units.fromPixels(bounds.height));
        box.componentDesc.nameProp.setInnerValue(AString.fromString(name));

        box.componentDesc.properties.get('model')
            .setPropValue(PropPath.fromString(BoxMainFieldPaths.OVERFLOW_Y), Overflow.hidden());
        box.componentDesc.properties.get('model')
            .setPropValue(PropPath.fromString(BoxStateFieldPaths.BORDER_COLOR_VISIBILITY), ABoolean.false());
        box.componentDesc.properties.get('model')
            .setPropValue(PropPath.fromString(BoxStateFieldPaths.BG_SOLID_VISIBILITY), ABoolean.false());

        box.addChildren(selectedBoxes);
        page.artboard.addBox(box);

        openDesign.addVisualArtifact(boxComponent.visualArtifactRef);

        selectedBoxes.forEach((box:Box) =>
        {
            box.setHostId(null);

            if( !parent ) //remove selected from root pane
            {
                const boxIndex = rootBoxes.findIndex(rootBox => (rootBox.id === box.id));
                Assert.yes(boxIndex >= 0);
                page.artboard.removeRootBox(boxIndex, false);
            }

            const {left, right, top, bottom, width, height} = boxesData[box.id];

            box.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, left);
            box.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, top);
            box.componentDesc.layoutRightProp.setPropValue(PropPath.ROOT, right);
            box.componentDesc.layoutBottomProp.setPropValue(PropPath.ROOT, bottom);
            box.componentDesc.layoutWidthProp.setPropValue(PropPath.ROOT, width);
            box.componentDesc.layoutHeightProp.setPropValue(PropPath.ROOT, height);
        });

        surfaceSelection.setSingleBoxSelection(box);
    }

    @action.bound
    public ungroupSelectedBox():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isUngroupActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getSelectedBoxes();
        const groupBox:Box = selectedBoxes[0];
        const page:Page = groupBox.page;
        const groupParent:Box = groupBox.parent;
        const groupHostId:BoxHostId = groupBox.hostId;
        const groupChildren:Array<Box> = [...groupBox.children];
        const groupBounds:Bounds = groupBox.bounds;

        if( groupParent )
            groupParent.addChildren(groupChildren);
        else
        {
            groupChildren.forEach(child =>
            {
                groupBox.removeChild(child);
                page.artboard.addRootBox(child);
            });
        }

        groupChildren.forEach((box:Box) => box.setHostId(groupHostId));

        //reset selection
        surfaceSelection.setMultipleBoxSelection(groupChildren);

        //change relative location for ungrouped boxes
        this.moveSelectedBoxesBy(SurfaceBounds.from(groupBounds.x, groupBounds.y, 0, 0));

        //remove the groupBox
        this.removeVisualComponent(groupBox);
    }

    @action.bound
    public bringSelectedBoxesToFront():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isBringToFrontActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getOrderedSelectedBoxes();

        selectedBoxes.forEach((box:Box) =>
        {
            if( box.parent )
                box.parent.moveChild(box, box.parent.children.length - 1);
            else
                box.page.artboard.moveChild(box, box.page.artboard.boxes.length - 1);
        });
    }

    @action.bound
    public bringSelectedBoxesForward():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isBringForwardActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getOrderedSelectedBoxes();
        const boxes = this.getBoxParentChildren(selectedBoxes[0]);

        selectedBoxes.slice().reverse().forEach((box:Box) =>
        {
            const boxIndex = boxes.findIndex(({id}) => id === box.id);
            const nextBox = boxes[boxIndex + 1];
            const isBoxLast = boxes[boxes.length - 1] === box;

            if( !isBoxLast && !surfaceSelection.isBoxSelected(nextBox) )
            {
                const newBoxes:Array<Box> = ArrayUtils.reorderArray(boxes, boxIndex, boxIndex + 1);

                if( box.parent )
                    box.parent.replaceChildren(newBoxes);
                else
                    box.page.artboard.replaceBoxes(newBoxes);
            }
        });
    }

    @action.bound
    public sendSelectedBoxesToBack():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isSendToBackActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getOrderedSelectedBoxes();

        selectedBoxes.forEach((box:Box) =>
        {
            if( box.parent )
                box.parent.moveChild(box, 0);
            else
                box.page.artboard.moveChild(box, 0);
        });
    }

    @action.bound
    public sendSelectedBoxesBackward():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isSendBackwardActionAvailable() )
            return false;

        const selectedBoxes:Array<Box> = surfaceSelection.getOrderedSelectedBoxes();
        const boxes = this.getBoxParentChildren(selectedBoxes[0]);

        selectedBoxes.forEach((box:Box) =>
        {
            const boxIndex = boxes.findIndex(({id}) => id === box.id);
            const previousBox = boxes[boxIndex - 1];
            const isBoxFirst = boxes[0] === box;

            if( !isBoxFirst && !surfaceSelection.isBoxSelected(previousBox) )
            {
                const newBoxes = ArrayUtils.reorderArray(boxes, boxIndex, boxIndex - 1);

                if( box.parent )
                    box.parent.replaceChildren(newBoxes);
                else
                    box.page.artboard.replaceBoxes(newBoxes);
            }
        });
    }

    @action.bound
    public alignSelectedBoxesHorizontally(alignValue:AString):boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isAlignmentActionAvailable() )
            return false;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();
        const bounds = this.getBoxesBounds(selectedBoxes);

        if( alignValue === HorizontalAlignment.LEFT )
        {
            for( const box of selectedBoxes )
                box.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, Units.fromPixels(bounds.x));
        }
        else if( alignValue === HorizontalAlignment.CENTER )
        {
            for( const box of selectedBoxes )
            {
                const boxWidth = box.componentDesc.layoutWidth.value.value;
                const left = Units.fromPixels(bounds.width / 2 + bounds.x - boxWidth / 2);

                box.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, left);
            }
        }
        else if( alignValue === HorizontalAlignment.RIGHT )
        {
            for( const box of selectedBoxes )
            {
                const boxWidth = box.componentDesc.layoutWidth.value.value;
                const left = Units.fromPixels(bounds.width + bounds.x - boxWidth);

                box.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, left);
            }
        }
        else
            throw new Error('unknown horizontal alignment value');

    }

    @action.bound
    public alignSelectedBoxesVertically(alignValue:AString):boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isAlignmentActionAvailable() )
            return false;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        const bounds = this.getBoxesBounds(selectedBoxes);

        if( alignValue === VerticalAlignment.TOP )
        {
            for( const box of selectedBoxes )
                box.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, Units.fromPixels(bounds.y));
        }
        else if( alignValue === VerticalAlignment.MIDDLE )
        {
            for( const box of selectedBoxes )
            {
                const boxHeight = box.componentDesc.layoutHeight.value.value;
                const top = Units.fromPixels(bounds.height / 2 + bounds.y - boxHeight / 2);

                box.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, top);
            }
        }
        else if( alignValue === VerticalAlignment.BOTTOM )
        {
            for( const box of selectedBoxes )
            {
                const boxHeight = box.componentDesc.layoutHeight.value.value;
                const top = Units.fromPixels(bounds.height + bounds.y - boxHeight);

                box.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, top);
            }
        }
        else
            throw new Error('unknown vertical alignment value');

    }

    @action.bound
    public distributeSelectedBoxesHorizontally():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isDistributionActionAvailable() )
            return false;

        const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

        const bounds = this.getBoxesBounds(selectedBoxes);
        const boxesTotalWidth = selectedBoxes.reduce((totalWidth, box) => (
            totalWidth + box.componentDesc.layoutWidth.value.value
        ), 0);

        const gapSize = Math.floor((bounds.width - boxesTotalWidth) / (selectedBoxes.length - 1));

        selectedBoxes.sort((box1, box2) =>
        {
            const box1Left = box1.componentDesc.layoutLeft.value.value;
            const box2Left = box2.componentDesc.layoutLeft.value.value;

            return (box1Left) - (box2Left);
        });

        for( const [index, box] of Array.from(selectedBoxes.entries()) )
        {
            if( !(index === 0 || index === selectedBoxes.length - 1) )
            {
                const previousBox = selectedBoxes[index - 1];
                const previousBoxLeft = previousBox.componentDesc.layoutLeft.value.value;
                const previousBoxWidth = previousBox.componentDesc.layoutWidth.value.value;
                const left = Units.fromPixels(previousBoxLeft + previousBoxWidth + gapSize);

                box.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, left);
            }
        }
    }

    @action.bound
    public distributeSelectedBoxesVertically():boolean
    {
        const {surfaceSelection} = controllers.ui;

        if( !this.isDistributionActionAvailable() )
            return false;

        const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

        const bounds = this.getBoxesBounds(selectedBoxes);
        const boxesTotalHeight = selectedBoxes.reduce((totalHeight, box) => (
            totalHeight + box.componentDesc.layoutHeight.value.value
        ), 0);
        const gapSize = Math.floor((bounds.height - boxesTotalHeight) / (selectedBoxes.length - 1));

        selectedBoxes.sort((box1, box2) =>
        {
            const box1Top = box1.componentDesc.layoutTop.value.value;
            const box2Top = box2.componentDesc.layoutTop.value.value;

            return (box1Top) - (box2Top);
        });

        for( const [index, box] of Array.from(selectedBoxes.entries()) )
        {
            if( !(index === 0 || index === selectedBoxes.length - 1) )
            {
                const previousBox = selectedBoxes[index - 1];
                const previousBoxTop = previousBox.componentDesc.layoutTop.value.value;
                const previousBoxHeight = previousBox.componentDesc.layoutHeight.value.value;
                const top = Units.fromPixels(previousBoxTop + previousBoxHeight + gapSize);

                box.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, top);
            }
        }
    }

    @action.bound
    public removeSelectedVisualComponents():void
    {
        const selectionController = controllers.ui.surfaceSelection;
        const selectedBoxes = selectionController.getSelectedBoxes();

        selectedBoxes.slice().forEach((selectedBox:Box) =>
        {
            !selectedBox.isViewport && this.removeVisualComponent(selectedBox);
        });
    }

    public removeVisualComponent(box:Box):void
    {
        const {surfaceSelection, surfacePagesPanel, bindings} = controllers.ui;

        if( surfaceSelection.isBoxSelected(box) )
            surfaceSelection.removeBoxFromSelection(box);

        surfacePagesPanel.removeOpenOutlineItem(box.id, null);

        if( bindings.isBindingInProgress() )
            bindings.stopBinding();

        super.removeVisualComponent(box);

    }

    public hasSurfacePointIntersectionWithBounds(point:SurfacePoint, bounds:Bounds):boolean
    {
        return (
            point.x >= bounds.x && point.x <= bounds.x + bounds.width &&
            point.y >= bounds.y && point.y <= bounds.y + bounds.height
        );
    }

    @action
    public setMouseOverRef(ref:MouseOverRef | null)
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        area.mouseOverRef = ref;
    }

    @action
    public setBoxRef(box:Box, ref:HTMLElement):void
    {
        box.uiState.ref = ref;
    }

    @action
    public setRepeatedBoxRef(box:Box, repeater:Repeater, ref:HTMLElement):void
    {
        box.uiState.repeatedRefs.set(repeater.key, ref);
    }

    @action
    public deleteRepeatedBoxRef(box:Box, repeater:Repeater):boolean
    {
        return box.uiState.repeatedRefs.delete(repeater.key);
    }

    @action
    public setBoxHostRef(box:Box, hostId:BoxHostId, ref:HTMLElement):void
    {
        box.uiState.hostRefs.set(hostId, ref);
    }

    @action
    public setRepeatedBoxHostRef(box:Box, repeater:Repeater, hostId:BoxHostId, ref:HTMLElement):void
    {
        box.uiState.repeatedHostRefs.set(`${repeater.key}_${hostId}`, ref);
    }

    @action
    public deleteRepeatedBoxHostRef(box:Box, repeater:Repeater, hostId:BoxHostId):boolean
    {
        return box.uiState.repeatedHostRefs.delete(`${repeater.key}_${hostId}`);
    }

    public getMouseOverRef():MouseOverRef | null
    {
        return rootState.ui.visualDesigner.surfaceEditor.area.mouseOverRef;
    }

    public isMouseOverRef(ref:MouseOverRef):boolean
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        if( area.mouseOverRef )
        {
            const {box, repeater, hostId} = area.mouseOverRef;

            return box.id === ref.box.id
                && repeater.key === ref.repeater.key
                && hostId === ref.hostId;
        }

        return false;
    }

    public isMouseOverBox(box:Box):boolean
    {
        const mouseOverRef:MouseOverRef = this.getMouseOverRef();

        return mouseOverRef ? mouseOverRef.box.id === box.id : false;
    }

    public isMouseOverHost(box:Box, hostId:BoxHostId):boolean
    {
        const mouseOverRef:MouseOverRef = this.getMouseOverRef();

        return mouseOverRef
            ? mouseOverRef.box.id === box.id && mouseOverRef.hostId === hostId
            : false;
    }

    public getBoxRef(box:Box):HTMLElement
    {
        return box.uiState.ref;
    }

    public getRepeatedBoxRef(box:Box, repeater:Repeater):HTMLElement
    {
        return box.uiState.repeatedRefs.get(repeater.key);
    }

    public getFirstOfLastLevelRepeatedBoxRef(box:Box, repeater:Repeater):HTMLElement
    {
        return box.uiState.repeatedRefs.get(repeater.firstOfLastLevelKey);
    }

    public getBoxHostRef(box:Box, hostId:BoxHostId):HTMLElement
    {
        return box.uiState.hostRefs.get(hostId);
    }

    public getRepeatedBoxHostRef(box:Box, repeater:Repeater, hostId:BoxHostId):HTMLElement
    {
        return box.uiState.repeatedHostRefs.get(`${repeater.key}_${hostId}`);
    }

    public getBoxRefAbsolutePoint(ref:HTMLElement):Point
    {
        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const pageInfo:PageInfo = this.getPageInfo();
        const bounds:ClientRect = ref.getBoundingClientRect();

        return Point.from(
            (bounds.left - pageInfo.pageOffset.x) / zoom,
            (bounds.top - pageInfo.pageOffset.y) / zoom
        );
    }

    @bind
    public findBoxVisualComponent():VisualComponent
    {
        return controllers.ui.workbench.getVisualComponentById(BOX_COMPONENT_ID);
    }

    public computePositionWithinCanvas(position:SurfacePoint, adjustForZoom:boolean = false):SurfacePoint
    {
        const {surfaceAreaDomElement, zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const boundingRect = surfaceAreaDomElement.getBoundingClientRect();

        const x0 = boundingRect.left;
        const y0 = boundingRect.top;

        const x = Math.round(position.x - x0);
        const y = Math.round(position.y - y0);

        if( adjustForZoom )
            return SurfacePoint.from(x / zoom, y / zoom);

        return SurfacePoint.from(x, y);
    }

    public onMouseMoveEvent(clientX:number, clientY:number):void
    {
        const designState = controllers.ui.openDesign.getDesignState();

        if( !designState || !designState.surface )
            return;

        const {surfaceAreaDomElement, zoom, panModeState} = rootState.ui.visualDesigner.surfaceEditor.area;

        const {left: surfaceX, top: surfaceY} = surfaceAreaDomElement.getBoundingClientRect();
        const {x: panX, y: panY} = panModeState.position;
        const {x: viewportX, y: viewportY} = designState.surface.activePage.artboard.viewport.bounds;

        const {thisUser} = rootState.ui.visualDesigner.activeUsers;
        thisUser.mouseX = (clientX - surfaceX - (viewportX + panX) * zoom) / zoom;
        thisUser.mouseY = (clientY - surfaceY - (viewportY + panY) * zoom) / zoom;
        thisUser.cursorAt = 'surface';

        EventBus.fire(this, 'mouseMove');
    }

    @action
    public onBoxDragInProgressEvent(boxes:Box[])
    {
        if( !boxes.length )
            return;

        const {area: {surfaceAreaDomElement, zoom, panModeState}} = rootState.ui.visualDesigner.surfaceEditor;
        const {thisUser} = rootState.ui.visualDesigner.activeUsers;

        const {left: surfaceX, top: surfaceY} = surfaceAreaDomElement.getBoundingClientRect();
        const {x: panX, y: panY} = panModeState.position;

        if( !thisUser.boxMoveState )
            thisUser.boxMoveState = new Map();

        boxes.forEach(box =>
        {
            if( !box.uiState.ref || !box.component.ref.resolvedComponent || !box.componentDesc.isSelfDirected )
            {
                thisUser.boxMoveState = undefined;
                return;
            }

            const isReparenting = controllers.ui.surfaceBoxReparenting.isBoxReparentingEnabled();

            if( isReparenting )
            {
                const {left: boxX, top: boxY, width: boxWidth, height: boxHeight} = box.uiState.ref.getBoundingClientRect();
                const clientLeft = (boxX - surfaceX - (panX) * zoom) / zoom;
                const clientTop = (boxY - surfaceY - (panY) * zoom) / zoom;

                thisUser.boxMoveState.set(box.id, {
                    isReparenting,
                    left: clientLeft,
                    top: clientTop,
                    width: boxWidth / zoom,
                    height: boxHeight / zoom
                } as IBoxMoveState);
            }
            else
            {
                thisUser.boxMoveState.set(box.id, {
                    isReparenting,
                    left: box.componentDesc.layoutLeft.value.value,
                    top: box.componentDesc.layoutTop.value.value,
                    right: box.componentDesc.layoutRight.value.value,
                    bottom: box.componentDesc.layoutBottom.value.value,
                    width: box.componentDesc.layoutWidth.value.value,
                    height: box.componentDesc.layoutHeight.value.value
                } as IBoxMoveState);
            }
        });

        EventBus.fire(this, 'boxDragInProgress');
    }

    @action
    public onBoxDragStopEvent()
    {
        const {thisUser} = rootState.ui.visualDesigner.activeUsers;

        thisUser.boxMoveState = undefined;

        EventBus.fire(this, 'boxDragStop');
    }

    @action
    public onBoxResizeInProgressEvent(box:Box)
    {
        if( !box.uiState.ref || !box.component.ref.resolvedComponent || !box.componentDesc.isSelfDirected )
            return;

        const {thisUser} = rootState.ui.visualDesigner.activeUsers;

        if( !thisUser.boxMoveState )
            thisUser.boxMoveState = new Map();

        thisUser.boxMoveState.set(box.id, {
            isReparenting: false,
            left: box.componentDesc.layoutLeft.value.value,
            top: box.componentDesc.layoutTop.value.value,
            right: box.componentDesc.layoutRight.value.value,
            bottom: box.componentDesc.layoutBottom.value.value,
            width: box.componentDesc.layoutWidth.value.value,
            height: box.componentDesc.layoutHeight.value.value
        } as IBoxMoveState);

        EventBus.fire(this, 'boxResizeInProgress');
    }

    @action
    public onBoxResizeStopEvent()
    {
        const {thisUser} = rootState.ui.visualDesigner.activeUsers;

        thisUser.boxMoveState = undefined;

        EventBus.fire(this, 'boxResizeStop');
    }

    private _generateNewPageName(originalPageName?:string):PageName
    {
        const state:SurfaceState = this.getState();
        if( !state )
            return '';

        const allPages:Page[] = Array.from(state.pages.values());
        const existingNames:string[] = allPages.map(({name}) => name.toLowerCase());

        let initialName:string;

        if( originalPageName )
        {
            const regex1 = '^.+ Copy';
            const regex2 = '( ([2-9]|[1-9]\\d+))?$';

            const isCloneAlready = originalPageName.match(new RegExp(`${regex1}${regex2}`, 'i'));

            if( isCloneAlready )
                initialName = originalPageName.replace(new RegExp(regex2, 'i'), '');
            else
                initialName = `${originalPageName} Copy`;

            let index = 2;
            let newBoxName:string = initialName;

            while( true )
            {
                if( existingNames.indexOf(newBoxName.toLowerCase()) === -1 )
                    break;

                newBoxName = `${initialName} ${index}`;
                index += 1;
            }

            return newBoxName;
        }

        initialName = 'Page';
        let newBoxName:string;
        let index = 1;

        while( true )
        {
            newBoxName = `${initialName} ${index}`;

            if( existingNames.indexOf(newBoxName.toLowerCase()) === -1 )
                break;

            index += 1;
        }

        return newBoxName;
    }

    private _isBoxChildOfViewport(box:Box):boolean
    {
        let parentBox:Box = box.parent;

        while( parentBox )
        {
            if( parentBox.isViewport )
                return true;

            parentBox = parentBox.parent;
        }

        return false;
    }

    private _rotate(top:number, left:number, width:number, height:number, angle:number, origin:RotationOrigin)
    {
        const piDiv180 = Math.PI / 180;

        const cos = Math.cos(angle * piDiv180);
        const sin = Math.sin(angle * piDiv180);

        let x0:number;
        let y0:number;

        switch( true )
        {
            case origin.value.isEqualTo(RotationOrigin.LEFT_TOP):
                x0 = left;
                y0 = top;
                break;
            case origin.value.isEqualTo(RotationOrigin.CENTER_TOP):
                x0 = left + width / 2;
                y0 = top;
                break;
            case origin.value.isEqualTo(RotationOrigin.RIGHT_TOP):
                x0 = left + width;
                y0 = top;
                break;
            case origin.value.isEqualTo(RotationOrigin.LEFT_CENTER):
                x0 = left;
                y0 = top + height / 2;
                break;
            case origin.value.isEqualTo(RotationOrigin.CENTER_CENTER):
                x0 = left + width / 2;
                y0 = top + height / 2;
                break;
            case origin.value.isEqualTo(RotationOrigin.RIGHT_CENTER):
                x0 = left + width;
                y0 = top + height / 2;
                break;
            case origin.value.isEqualTo(RotationOrigin.LEFT_BOTTOM):
                x0 = left;
                y0 = top + height;
                break;
            case origin.value.isEqualTo(RotationOrigin.CENTER_BOTTOM):
                x0 = left + width / 2;
                y0 = top + height;
                break;
            case origin.value.isEqualTo(RotationOrigin.RIGHT_BOTTOM):
                x0 = left + width;
                y0 = top + height;
                break;
        }

        let leftTopX:number = left;
        let leftTopY:number = top;

        let rightTopX:number = left + width;
        let rightTopY:number = top;

        let leftBottomX:number = left;
        let leftBottomY:number = top + height;

        let rightBottomX:number = left + width;
        let rightBottomY:number = top + height;

        // x = x0 + (x - x0) * cos - (y - y0) * sin
        // y = y0 + (y - y0) * cos + (x - x0) * sin

        // x1 ---- x2
        // |        |
        // |        |
        // x4 ---- x3

        //    x1 __
        //   /      -- x2
        //  /          /
        // x4 __      /
        //       -- x3

        const x1 = x0 + (leftTopX - x0) * cos - (leftTopY - y0) * sin;
        const y1 = y0 + (leftTopY - y0) * cos + (leftTopX - x0) * sin;

        const x2 = x0 + (rightTopX - x0) * cos - (rightTopY - y0) * sin;
        const y2 = y0 + (rightTopY - y0) * cos + (rightTopX - x0) * sin;

        const x3 = x0 + (rightBottomX - x0) * cos - (rightBottomY - y0) * sin;
        const y3 = y0 + (rightBottomY - y0) * cos + (rightBottomX - x0) * sin;

        const x4 = x0 + (leftBottomX - x0) * cos - (leftBottomY - y0) * sin;
        const y4 = y0 + (leftBottomY - y0) * cos + (leftBottomX - x0) * sin;

        leftTopX = Math.min(x1, x2, x3, x4);
        leftTopY = Math.min(y1, y2, y3, y4);

        rightBottomX = Math.max(x1, x2, x3, x4);
        rightBottomY = Math.max(y1, y2, y3, y4);

        return {
            top: leftTopY,
            left: leftTopX,
            width: Math.abs(rightBottomX - leftTopX),
            height: Math.abs(rightBottomY - leftTopY)
        };
    }

    private _removeMovingBox(movingBox:Box):void
    {
        const movingBoxIndex:number = this.getBoxIndex(movingBox);

        if( movingBox.parent )
            movingBox.parent.removeChild(movingBox);
        else
            movingBox.page.artboard.removeRootBox(movingBoxIndex, false);
    }

    public isBoxOnMoveByAnotherUser(box:Box)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        return activeUsers.otherUserBoxMoveState.has(box.id) || activeUsers.otherUserBoxReparentingState.has(box.id);
    }

    public isBoxParentOnMoveByAnotherUser(box:Box)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        let isChildOfMovingBox:boolean = false;

        BoxTraverser.visit(box, {
            onBox: (currentBox:Box) =>
            {
                if( activeUsers.otherUserBoxMoveState.has(currentBox.id) || activeUsers.otherUserBoxReparentingState.has(currentBox.id) )
                    isChildOfMovingBox = true;
            }
        }, true);

        return isChildOfMovingBox;
    }

    public isBoxChildOnMoveByAnotherUser(box:Box)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        let isParentOfMovingBox:boolean = false;

        BoxTraverser.visit(box, {
            onBox: (currentBox:Box) =>
            {
                if( activeUsers.otherUserBoxMoveState.has(currentBox.id) || activeUsers.otherUserBoxReparentingState.has(currentBox.id) )
                    isParentOfMovingBox = true;
            }
        }, false);

        return isParentOfMovingBox;
    }
}
